import { Button, FormField, Surface } from '@chopchop-se/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ReceiptDetail } from '@warehouse/shared';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { toDateInputValue } from '../../../lib/format';
import { toastError, toastSuccess } from '../../../lib/notify';
import { receiptsApi, receivingApi, receivingKeys } from '../api';
import { ArticlePicker } from '../components/Pickers';
import { ScanField } from '../components/ScanField';
import type { ArticleOption } from '../master-data-port';

/**
 * Produce fast path (spec §5.17): vegetables and milk are packed the same day,
 * so they go straight to the packing floor. No pallet, no label, no batch
 * number on the box — the lot is supplier + delivery date, which is exactly
 * what the order number traces back to.
 */
export function ProducePanel({ receipt }: { receipt: ReceiptDetail }): ReactNode {
  const { t } = useTranslation('receiving');
  const queryClient = useQueryClient();

  const [article, setArticle] = useState<ArticleOption | null>(null);
  const [qty, setQty] = useState('');
  const [deliveryDate, setDeliveryDate] = useState(toDateInputValue(new Date()));
  const [scanCount, setScanCount] = useState(0);

  const closed = receipt.status === 'closed';

  // The produce box carries a supplier code, not a GS1 label — one scan
  // identifies the article and the operator types the count.
  const resolve = useMutation({
    mutationFn: (code: string) => receivingApi.scan({ code, receiptId: receipt.id }),
    onSuccess: (result) => {
      if (!result.article) {
        toastError(new Error(t('scan.unknown')));
        return;
      }
      setArticle({
        id: result.article.articleId,
        articleNo: result.article.articleNo,
        name: result.article.name,
        baseUnitId: result.article.baseUnitId,
        baseUnitCode: result.article.baseUnitCode,
        supplierId: null,
        lotTracked: result.article.lotTracked,
        expiryRequired: result.article.expiryRequired,
      });
      setQty((current) => current || result.article!.qtyPerScan);
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const receive = useMutation({
    mutationFn: () =>
      receiptsApi.produce(receipt.id, {
        articleId: article!.id,
        qty,
        unitId: article!.baseUnitId,
        deliveryDate: deliveryDate || null,
      }),
    onSuccess: async (result) => {
      toastSuccess(t('produce.received', { bin: result.binCode, lot: result.lotNo }));
      setQty('');
      setScanCount((n) => n + 1);
      await queryClient.invalidateQueries({ queryKey: receivingKeys.receipt(receipt.id) });
    },
    onError: (error) => {
      toastError(error);
    },
  });

  const valid = Boolean(article) && Number(qty) > 0;

  return (
    <Surface className="space-y-4 p-4">
      <div>
        <h3 className="text-sm font-medium text-ink">{t('produce.title')}</h3>
        <p className="mt-1 text-xs text-ink-muted">{t('produce.hint')}</p>
      </div>

      <ScanField
        label={t('scan.label')}
        placeholder={t('scan.placeholder')}
        disabled={closed}
        busy={resolve.isPending}
        refocusKey={scanCount}
        onScan={(code) => {
          resolve.mutate(code);
        }}
        testId="produce-scan"
      />

      <div className="grid gap-4 sm:grid-cols-3">
        <ArticlePicker
          label={t('produce.article')}
          value={article?.id ?? null}
          onChange={setArticle}
          disabled={closed}
        />
        <FormField
          label={t('produce.qty')}
          type="number"
          min="0"
          step="0.001"
          inputMode="decimal"
          value={qty}
          onChange={(event) => {
            setQty(event.target.value);
          }}
          disabled={closed}
          hint={article ? <span className="text-xs text-ink-muted">{article.baseUnitCode}</span> : undefined}
        />
        <FormField
          label={t('produce.deliveryDate')}
          type="date"
          value={deliveryDate}
          onChange={(event) => {
            setDeliveryDate(event.target.value);
          }}
          disabled={closed}
        />
      </div>

      <div className="flex justify-end">
        <Button
          loading={receive.isPending}
          disabled={closed || !valid}
          onClick={() => {
            receive.mutate();
          }}
        >
          {t('produce.submit')}
        </Button>
      </div>
    </Surface>
  );
}
